import React, { useState } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Grid2 as Grid,
	Typography,
	TextField,
	List,
	ListItem,
	ListItemText,
	Divider,
	Box,
	IconButton,
	useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { LargeActionButton } from "../Common/LargeActionButton";
import { LargeActionLink } from "../Common/LargeActionLink";
import { format, parseISO } from "date-fns";
import { Close } from "@mui/icons-material";
import { MOBILE_MODAL_MEDIA_QUERY } from "./Modal.constants";

const useStyles = makeStyles((theme) => ({
	commentModal: {
		"& .MuiDialog-paper": {
			minWidth: "70%",
			maxWidth: "80%",
		},
	},
	commentList: {
		overflowY: "auto",
		marginBottom: theme.spacing(2),
	},
	commentItem: {
		alignItems: "flex-start",
		paddingTop: theme.spacing(1),
		paddingBottom: theme.spacing(1),
	},
	commentText: {
		wordBreak: "break-word",
	},
	commentMeta: {
		color: theme.palette.text.secondary,
		fontSize: "0.75rem",
	},
	textField: {
		width: "100%",
		"& .MuiInputBase-root": {
			minHeight: "100px",
		},
	},
	noComments: {
		textAlign: "center",
		color: theme.palette.text.secondary,
		padding: theme.spacing(2),
	},
}));

interface Comment {
	id: string;
	receiveId: string;
	createdTimestamp: string;
	createdByUserId: string;
	createdByUserName: string;
	text: string;
	sourceSystem?: string;
}

interface CommentModalProps {
	isOpen: boolean;
	isDisabled: boolean;
	onClose: () => void;
	comments: Comment[];
	onAddComment: (commentText: string) => Promise<void>;
}

const displaySourceSystem = (s?: string) => {
	if (!s || s === "ReceivingApp") return "Receiving App";
	return s;
};

export const CommentModal: React.FC<CommentModalProps> = ({ isOpen, isDisabled, onClose, comments, onAddComment }) => {
	const classes = useStyles();
	const mobileView = useMediaQuery(MOBILE_MODAL_MEDIA_QUERY);
	const [newCommentText, setNewCommentText] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleAddComment = async () => {
		if (!newCommentText.trim()) return;

		setIsSubmitting(true);
		try {
			await onAddComment(newCommentText.trim());
			setNewCommentText("");
		} catch (error) {
			console.error("Failed to add comment:", error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const formatDate = (dateString: string) => {
		try {
			const date = parseISO(dateString);
			return format(date, "EEE dd/MM/yy hh:mm a");
		} catch {
			return dateString;
		}
	};

	const sortedComments = [...comments].sort(
		(a, b) => new Date(b.createdTimestamp).getTime() - new Date(a.createdTimestamp).getTime()
	);

	return (
		<Dialog
			open={isOpen}
			onClose={onClose}
			className={mobileView ? undefined : classes.commentModal}
			maxWidth="sm"
			fullWidth
			fullScreen={mobileView}
		>
			<DialogTitle>
				<Grid container justifyContent="space-between" alignItems="center">
					<Grid size="grow">
						<Typography variant="h5">Comments</Typography>
					</Grid>
					<Grid>
						<IconButton onClick={onClose}>
							<Close />
						</IconButton>
					</Grid>
				</Grid>
			</DialogTitle>
			<DialogContent>
				<Grid container direction="column" spacing={2}>
					{!isDisabled && (
						<Grid size="grow">
							<Typography variant="subtitle2" gutterBottom>
								Add New Comment
							</Typography>
							<TextField
								multiline
								minRows={4}
								placeholder="Enter your comment here..."
								value={newCommentText}
								onChange={(e) => setNewCommentText(e.target.value)}
								className={classes.textField}
								variant="outlined"
								disabled={isSubmitting}
							/>
						</Grid>
					)}
					<Grid size="grow">
						<Typography variant="subtitle2" gutterBottom>
							Comment History
						</Typography>
						{sortedComments.length > 0 ? (
							<List className={classes.commentList}>
								{sortedComments.map((comment, index) => (
									<React.Fragment key={comment.id}>
										<ListItem className={classes.commentItem}>
											<ListItemText
												primary={
													<Box className={classes.commentMeta}>
														{formatDate(comment.createdTimestamp)} - {comment.createdByUserName} via {displaySourceSystem(comment.sourceSystem)}
													</Box>
												}
												secondary={
													<Typography
														variant="body2"
														color="textPrimary"
														className={classes.commentText}
														component="span"
													>
														{comment.text}
													</Typography>
												}
											/>
										</ListItem>
										{index < sortedComments.length - 1 && <Divider />}
									</React.Fragment>
								))}
							</List>
						) : (
							<Box className={classes.noComments}>
								<Typography variant="body2" component="span">
									No comments yet
								</Typography>
							</Box>
						)}
					</Grid>
				</Grid>
			</DialogContent>
			<DialogActions>
				<Grid container spacing={1} justifyContent="space-between" sx={{ width: "100%" }}>
					<Grid size={{ xs: 6, sm: 6 }}>
						<LargeActionLink onClick={onClose}>Close</LargeActionLink>
					</Grid>
					{!isDisabled && (
						<Grid size={{ xs: 6, sm: 6 }} container justifyContent="flex-end">
							<LargeActionButton onClick={handleAddComment} disabled={!newCommentText.trim() || isSubmitting}>
								{isSubmitting ? "Adding..." : "Add Comment"}
							</LargeActionButton>
						</Grid>
					)}
				</Grid>
			</DialogActions>
		</Dialog>
	);
};
